import { ButtonOutlined, ButtonPrimary } from 'components/Button'
import { DarkCard } from 'components/Card'
import { AutoColumn } from 'components/Column'
import CurrencyLogo from 'components/CurrencyLogo'
import Row, { AutoRow } from 'components/Row'
import { Checkbox } from 'components/SearchModal/styleds'
import { TextLink } from 'components/Typography'
import { REFETCH_POLICY_FAST } from 'config/RefetchPolicies'
import { AIRDROP_CLAIM_ADDRESSES } from 'constants/addresses'
import { useToken } from 'hooks/Tokens'
import { useConnection } from 'hooks/useConnection'
import { useWriteContract } from 'hooks/useTransaction'
import { BackButton } from 'pages/Islands/IslandPage/styleds'
import { useCallback, useContext, useMemo, useState } from 'react'
import { ArrowLeft } from 'react-feather'
import { Link } from 'react-router-dom'
import { useTransactionAdder } from 'state/transactions/hooks'
import { TransactionType } from 'state/transactions/types'
import styled, { ThemeContext } from 'styled-components'
import { ThemedText } from 'theme'
import { StyledInternalLink } from 'theme/components'
import { formatAmount } from 'utils/formatDollarAmt'
import { Address, formatUnits, Hash } from 'viem'
import { useReadContract, useTransactionReceipt } from 'wagmi'

import ShareModal from './ShareModal'
import { SignMessageStatus, useSignClaimMessage } from './sign/useSignClaimMessage'
import { Skeleton } from './Skeleton'

const PageWrapper = styled(AutoColumn)`
  width: 100%;
  padding: 24px;
  max-width: 480px;
  margin: 0 auto;
`

const ClaimCard = styled(DarkCard)`
  padding: 32px;
  border-radius: 16px;
  gap: 24px;
  display: flex;
  flex-direction: column;
`

const TokenInfoWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 24px 0;
`

const AmountText = styled.div`
  font-size: 48px;
  font-weight: 600;
  line-height: 1;
  color: ${({ theme }) => theme.textPrimary};
`

const SymbolText = styled.div`
  font-size: 20px;
  font-weight: 500;
  color: ${({ theme }) => theme.textSecondary};
`

const MessageText = styled.div`
  padding: 12px;
  background-color: ${({ theme }) => theme.backgroundModule};
  border-radius: 12px;
  font-size: 12px;
  color: ${({ theme }) => theme.textSecondary};
  max-height: 100px;
  overflow-y: auto;
  white-space: pre-wrap;

  &::-webkit-scrollbar {
    width: 8px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background-color: ${({ theme }) => theme.textTertiary};
    border-radius: 4px;
  }
`

const ClaimErrorText = styled(ThemedText.DeprecatedError)`
  margin-top: 8px;
  text-align: center;
`

const SeasonBanner = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 14px 16px;
  border-radius: 12px;
  text-align: center;
  background-color: ${({ theme }) => theme.accentActionSoft};
  border: 1px solid ${({ theme }) => theme.accentAction};
`

const SeasonBannerTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.accentAction};
`

const SeasonBannerSubtitle = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.textSecondary};
`

const AIRDROP_CLAIM_ABI = [
  {
    inputs: [{ name: '', type: 'address' }],
    name: 'airdropAmounts',
    outputs: [{ name: '', type: 'uint256' }],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [],
    name: 'claim',
    outputs: [],
    stateMutability: 'nonpayable',
    type: 'function',
  },
  {
    inputs: [],
    name: 'airdropToken',
    outputs: [{ name: '', type: 'address' }],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [],
    name: 'deadline',
    outputs: [{ name: '', type: 'uint256' }],
    stateMutability: 'view',
    type: 'function',
  },
] as const

export default function ClaimPage() {
  const { chainId, account, wagmiPublicClient } = useConnection()
  const theme = useContext(ThemeContext)

  const isDisconnected = useMemo(() => !account, [account])

  const claimAddress = useMemo(() => (chainId ? AIRDROP_CLAIM_ADDRESSES[chainId] : undefined), [chainId])

  const { data: airdropTokenAddress } = useReadContract({
    address: claimAddress,
    abi: AIRDROP_CLAIM_ABI,
    functionName: 'airdropToken',
    query: { enabled: Boolean(claimAddress) },
  })
  const airdropToken = useToken(airdropTokenAddress as Address | undefined)
  const tokenSymbol = airdropToken?.symbol ?? 'KDK'
  const tokenDecimals = airdropToken?.decimals ?? 18

  const { data: claimableRaw } = useReadContract({
    address: claimAddress,
    abi: AIRDROP_CLAIM_ABI,
    functionName: 'airdropAmounts',
    args: account ? [account as Address] : undefined,
    query: { ...REFETCH_POLICY_FAST, enabled: Boolean(claimAddress && account) },
  })

  const { data: deadlineRaw } = useReadContract({
    address: claimAddress,
    abi: AIRDROP_CLAIM_ABI,
    functionName: 'deadline',
    query: { enabled: Boolean(claimAddress) },
  })

  const isExpired = useMemo(() => {
    if (deadlineRaw === undefined) return false
    return BigInt(Math.floor(Date.now() / 1000)) > (deadlineRaw as bigint)
  }, [deadlineRaw])

  const displayAmount = useMemo(
    () => (claimableRaw !== undefined ? formatAmount(formatUnits(claimableRaw as bigint, tokenDecimals), 2) : '0'),
    [claimableRaw, tokenDecimals]
  )

  const isNotZero = (claimableRaw as bigint | undefined) ? (claimableRaw as bigint) > 0n : false

  const [signStatus, signMessage, , termsLink] = useSignClaimMessage()
  const disclaimerAccount = account ?? ''

  const { writeContractAsync } = useWriteContract()

  const [attemptingTxn, setAttemptingTxn] = useState(false)
  const [txHash, setTxHash] = useState<Hash | undefined>(undefined)
  const [claimError, setClaimError] = useState<string | null>(null)
  const addTransaction = useTransactionAdder()
  const [showShareModal, setShowShareModal] = useState(false)
  const [claimedAmount, setClaimedAmount] = useState<string>('0')
  const [confirmed, setConfirmed] = useState(false)
  const switchConfirmed = useCallback(() => {
    setConfirmed((prev) => !prev)
  }, [])

  const handleClaim = useCallback(async () => {
    if (!claimAddress || !account || !chainId || !wagmiPublicClient) return

    setClaimError(null)
    setTxHash(undefined)
    setClaimedAmount(displayAmount)

    try {
      await wagmiPublicClient.simulateContract({
        address: claimAddress,
        abi: AIRDROP_CLAIM_ABI,
        functionName: 'claim',
        args: [],
        account,
      })
    } catch (error) {
      setClaimError('Claim is not available yet.')
      return
    }

    setAttemptingTxn(true)
    try {
      const hash = await writeContractAsync({
        address: claimAddress,
        abi: AIRDROP_CLAIM_ABI,
        functionName: 'claim',
        args: [],
      })

      setTxHash(hash)

      addTransaction(
        { hash },
        {
          type: TransactionType.AIRDROP_CLAIM,
          token: (airdropTokenAddress as Address) || '',
          amount: (claimableRaw as bigint | undefined)?.toString() || '0',
        }
      )
    } catch (error) {
      setClaimError('Claim failed. No tokens were claimed. Please try again.')
      setTxHash(undefined)
      console.error('Failed to claim tokens:', error)
    } finally {
      setAttemptingTxn(false)
    }
  }, [
    claimAddress,
    account,
    chainId,
    wagmiPublicClient,
    writeContractAsync,
    addTransaction,
    airdropTokenAddress,
    claimableRaw,
    displayAmount,
  ])

  const {
    data: receipt,
    isPending: isReceiptPending,
    isEnabled: isReceiptEnabled,
  } = useTransactionReceipt({
    hash: txHash as Hash,
    query: {
      enabled: Boolean(txHash),
    },
  })
  const isReceiptSuccess = receipt?.status === 'success'
  const isReceiptFailed = receipt?.status === 'reverted'

  const isLoading = useMemo(() => {
    return Boolean(account && claimableRaw === undefined)
  }, [claimableRaw, account])

  return (
    <PageWrapper>
      <StyledInternalLink
        data-cy="back-button"
        style={{ textDecoration: 'none', width: 'fit-content', marginBottom: '0.1rem' }}
        to="/staking"
      >
        <BackButton>
          <ArrowLeft color={theme.textSecondary} size={20} />
          Staking
        </BackButton>
      </StyledInternalLink>
      <ClaimCard>
        <SeasonBanner>
          <SeasonBannerTitle>Kodiak Perps Season 2 points have ended</SeasonBannerTitle>
          {/* <SeasonBannerSubtitle>Thanks for trading through Season 2. Claim your rewards below.</SeasonBannerSubtitle> */}
        </SeasonBanner>
        <AutoColumn gap="sm">
          <ThemedText.DeprecatedLargeHeader textAlign="center">
            {isReceiptSuccess ? `Claimed ${tokenSymbol}` : `Claim ${tokenSymbol}`}
          </ThemedText.DeprecatedLargeHeader>
          <ThemedText.DeprecatedBody textAlign="center" color="textSecondary">
            {isReceiptSuccess ? 'Your claim is complete.' : `Claim your Season 2 ${tokenSymbol} rewards`}
          </ThemedText.DeprecatedBody>
        </AutoColumn>

        <TokenInfoWrapper>
          <AutoColumn gap="4px" justify="center" style={{ alignItems: 'center' }}>
            {isLoading ? (
              <Skeleton width="150px" height="48px" />
            ) : (
              <AmountText>{isReceiptSuccess ? claimedAmount : displayAmount}</AmountText>
            )}
            <Row gap="8px" align="center" justifyContent="center" alignItems="center">
              <CurrencyLogo currency={airdropToken} size="21px" />
              <SymbolText>
                {tokenSymbol} {isReceiptSuccess ? 'Claimed' : 'Available'}
              </SymbolText>
            </Row>
          </AutoColumn>
        </TokenInfoWrapper>

        <AutoColumn gap="md">
          {isDisconnected ? (
            <ButtonPrimary disabled={true} padding="16px" $borderRadius="12px">
              <ThemedText.DeprecatedMediumHeader color="white">
                Connect Wallet to Claim
              </ThemedText.DeprecatedMediumHeader>
            </ButtonPrimary>
          ) : isLoading ? (
            <Skeleton width="100%" height="56px" />
          ) : isReceiptSuccess ? (
            <>
              <ButtonPrimary as={Link} to="/staking" padding="16px" $borderRadius="12px">
                <ThemedText.DeprecatedMediumHeader color="white">Go to Staking</ThemedText.DeprecatedMediumHeader>
              </ButtonPrimary>
              <ButtonOutlined padding="16px" $borderRadius="12px" onClick={() => setShowShareModal(true)}>
                <ThemedText.DeprecatedMediumHeader>Share</ThemedText.DeprecatedMediumHeader>
              </ButtonOutlined>
            </>
          ) : isExpired ? (
            <ButtonPrimary disabled={true} padding="16px" $borderRadius="12px">
              <ThemedText.DeprecatedMediumHeader color="white">
                Claim period has ended
              </ThemedText.DeprecatedMediumHeader>
            </ButtonPrimary>
          ) : !isNotZero ? (
            <ButtonPrimary disabled={true} padding="16px" $borderRadius="12px">
              <ThemedText.DeprecatedMediumHeader color="white">No Tokens to Claim</ThemedText.DeprecatedMediumHeader>
            </ButtonPrimary>
          ) : signStatus !== SignMessageStatus.SIGNED ? (
            <>
              <MessageText>
                I, {disclaimerAccount}, have read the relevant{' '}
                {
                  <>
                    <TextLink style={{ fontSize: '12px' }} href={termsLink} target="_blank" rel="noreferrer">
                      terms and conditions
                    </TextLink>
                  </>
                }{' '}
                and fully agree with them.
              </MessageText>
              <AutoRow gap="4px" padding="0 8px" as="label">
                <Checkbox type="checkbox" checked={confirmed} onChange={switchConfirmed} />
                <ThemedText.DeprecatedBody fontSize={14} style={{ cursor: 'pointer', userSelect: 'none' }}>
                  I have read and confirm everything
                </ThemedText.DeprecatedBody>
              </AutoRow>
              <ButtonPrimary
                disabled={signStatus === SignMessageStatus.PENDING || !confirmed}
                padding="16px"
                $borderRadius="12px"
                onClick={signMessage}
              >
                <ThemedText.DeprecatedMediumHeader color="white">Sign message</ThemedText.DeprecatedMediumHeader>
              </ButtonPrimary>
            </>
          ) : (
            <ButtonPrimary
              disabled={attemptingTxn || (isReceiptPending && isReceiptEnabled)}
              onClick={handleClaim}
              padding="16px"
              $borderRadius="12px"
            >
              <ThemedText.DeprecatedMediumHeader color="white">Claim</ThemedText.DeprecatedMediumHeader>
            </ButtonPrimary>
          )}
          {(claimError || isReceiptFailed) && (
            <ClaimErrorText error={true} fontSize={12}>
              {claimError || 'Claim failed. No tokens were claimed. Please try again later.'}
            </ClaimErrorText>
          )}
        </AutoColumn>
      </ClaimCard>
      <ShareModal isOpen={showShareModal} onDismiss={() => setShowShareModal(false)} amount={claimedAmount} />
    </PageWrapper>
  )
}
